import {
  ReferenceDetail,
  SimpleReferenceDetailStatus,
} from "../interfaces/referenceDetaill";
import { ReferenceLetter } from "../interfaces/referenceLetter";
import { fetchWithAuth } from "./fetchWithAuth";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getReferenceLettersByReferenceDetailID = async (
  referenceDetailID: number,
): Promise<ReferenceLetter[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const referenceLettersResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceLetter/getReferenceLettersByReferenceDetailID/${referenceDetailID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!referenceLettersResponse.ok) {
    throw new Error("Failed to fetch reference letters");
  }

  const referenceLetters = await referenceLettersResponse.json();

  return referenceLetters;
};

export const loadReferenceLetters = async (
  referenceDetails: ReferenceDetail[] | SimpleReferenceDetailStatus[],
): Promise<Record<number, ReferenceLetter[]>> => {
  const letterPromises = referenceDetails.map(async (detail) => {
    const letters: ReferenceLetter[] =
      await getReferenceLettersByReferenceDetailID(detail.referenceDetailID);

    letters.sort(
      (a, b) =>
        new Date(a.letterDate).getTime() - new Date(b.letterDate).getTime(),
    );

    return {
      referenceDetailID: detail.referenceDetailID,
      letters,
    };
  });

  const resolvedLetters = await Promise.all(letterPromises);
  return resolvedLetters.reduce(
    (acc, { referenceDetailID, letters }) => {
      acc[referenceDetailID] = letters;
      return acc;
    },
    {} as Record<number, ReferenceLetter[]>,
  );
};

export const createReferenceLetter = async (
  referenceLetter: ReferenceLetter,
): Promise<ReferenceLetter> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/ReferenceLetter/CreateReferenceLetter`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(referenceLetter),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to create reference letter.");
  }
  return response.json();
};

export const updateReferenceLetters = async (
  referenceLetters: ReferenceLetter[],
): Promise<ReferenceLetter[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/ReferenceLetter/UpdateReferenceLetters`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(referenceLetters),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to save reference letters.");
  }
  return response.json();
};
